/**
 * Native Helpers — Küçük Capacitor plugin sarmalayıcıları
 *
 * Bu modül React app'in her yerinden çağrılabilen native yardımcıları sunar.
 * Her fonksiyon web'de silently no-op olur (Capacitor.isNativePlatform() false).
 *
 * Web'e "silent no-op" davranışı sayesinde `if (isNative)` kontrolü gerekmez —
 * çağıran kod her ortamda çalışır.
 */
import { Capacitor } from "@capacitor/core";

const isNative = () => {
  try {
    return Capacitor?.isNativePlatform && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// ─── Haptic Feedback ─────────────────────────────────────────────────────
// Web fallback: navigator.vibrate() ile hafif titreşim (destekleyen tarayıcılarda)
export async function hapticLight() {
  if (isNative()) {
    try {
      const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch { /* plugin yoksa geç */ }
  } else if (navigator.vibrate) {
    navigator.vibrate(10);
  }
}

export async function hapticMedium() {
  if (isNative()) {
    try {
      const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch { /* plugin yoksa geç */ }
  } else if (navigator.vibrate) {
    navigator.vibrate(25);
  }
}

export async function hapticSuccess() {
  if (isNative()) {
    try {
      const { Haptics, NotificationType } = await import("@capacitor/haptics");
      await Haptics.notification({ type: NotificationType.Success });
    } catch { /* plugin yoksa geç */ }
  } else if (navigator.vibrate) {
    navigator.vibrate([20, 40, 20]);
  }
}

export async function hapticError() {
  if (isNative()) {
    try {
      const { Haptics, NotificationType } = await import("@capacitor/haptics");
      await Haptics.notification({ type: NotificationType.Error });
    } catch { /* plugin yoksa geç */ }
  } else if (navigator.vibrate) {
    navigator.vibrate([100, 50, 100]);
  }
}

// ─── Keyboard ────────────────────────────────────────────────────────────
export async function hideKeyboard() {
  if (!isNative()) return;
  try {
    const { Keyboard } = await import("@capacitor/keyboard");
    await Keyboard.hide();
  } catch { /* plugin yoksa geç */ }
}

// ─── Network Status ──────────────────────────────────────────────────────
// initNetworkListener çağıran component'e `(status) => void` callback verir.
// Callback her network değişikliğinde çağrılır: { connected: boolean, connectionType: 'wifi'|'cellular'|'none'|... }
export async function initNetworkListener(onChange) {
  // Web: Native Network API + online/offline events
  if (!isNative()) {
    const emit = () =>
      onChange({
        connected: navigator.onLine,
        connectionType: navigator.onLine ? "unknown" : "none",
      });
    // İlk state
    emit();
    const on = () => emit();
    window.addEventListener("online", on);
    window.addEventListener("offline", on);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", on);
    };
  }

  try {
    const { Network } = await import("@capacitor/network");
    const initial = await Network.getStatus();
    onChange(initial);
    const handle = await Network.addListener("networkStatusChange", (status) => {
      onChange(status);
    });
    return () => {
      handle.remove().catch(() => { /* noop */ });
    };
  } catch {
    // Plugin yoksa fallback: sadece bir seferlik "connected" bildir
    onChange({ connected: navigator.onLine, connectionType: "unknown" });
    return () => { /* noop */ };
  }
}

// ─── App Version ─────────────────────────────────────────────────────────
export async function getAppVersion() {
  if (!isNative()) return { version: "web", build: "0", isNative: false };
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return {
      version: info.version || "0.0.0",
      build: info.build || "0",
      name: info.name || "",
      id: info.id || "",
      isNative: true,
    };
  } catch {
    return { version: "unknown", build: "0", isNative: true };
  }
}

// ─── Platform helpers ────────────────────────────────────────────────────
export function isNativePlatform() {
  return isNative();
}

export function getPlatform() {
  try {
    return Capacitor?.getPlatform ? Capacitor.getPlatform() : "web";
  } catch {
    return "web";
  }
}
